/*
 * nadi-free-shipping.js — tell people how close they are to free shipping.
 *
 * WHY THIS EXISTS. Measured on the live cart, 2026-08-10: a ₪49 cart is ₪200
 * short of the free-shipping threshold and the cart page says nothing about
 * it. No progress bar, no message — the string "249" does not appear anywhere
 * in the cart form. The store gives the benefit away and never mentions it at
 * the one moment it changes a decision.
 *
 * The funnel says this is where the money is. Over 90 days, 4,406 sessions
 * added to cart and only 1,533 reached checkout — 34.8%. Average order is
 * ₪287.70 against a ₪249 threshold, so a large share of carts sit within one
 * small item of qualifying and are never told.
 *
 * The threshold is real and already live: an active automatic discount,
 * "משלוח חינם מעל קנייה של 249 ש\"ח על כל האתר!", with no end date.
 *
 * ── HOW IT KNOWS THE TOTAL ────────────────────────────────────────────────
 * /cart.js, always. Not by scraping the rendered subtotal, which is formatted
 * for humans — "1,234.00 ₪" — and would need currency and thousands-separator
 * parsing that breaks the first time a setting changes.
 *
 * ── HOW IT KNOWS TO UPDATE ────────────────────────────────────────────────
 * Kalles does not dispatch a documented cart event, and guessing at one that
 * may not fire is how a bar ends up showing a stale number after someone
 * changes a quantity — worse than no bar at all, because it is a promise that
 * is now wrong.
 *
 * So window.fetch is wrapped: any request to /cart/add, /cart/change,
 * /cart/update or /cart/clear triggers a refresh once it resolves. Every theme
 * and app cart action on Shopify goes through one of those four endpoints, so
 * this catches all of them without knowing anything about Kalles. The wrapper
 * passes the original call through untouched and never swallows an error.
 *
 * XMLHttpRequest is covered too — some apps still use it.
 *
 * ── WHERE IT MOUNTS ───────────────────────────────────────────────────────
 * The cart page and the cart drawer. The drawer is built lazily, so a
 * MutationObserver watches for it rather than assuming it exists at load.
 * Mounting is idempotent: a bar is only inserted where one is not already
 * present, so a re-render cannot produce two.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ──────────────────────────────────────
 * It does not claim free shipping is unconditional. The shipping policy
 * excludes remote areas, so the qualified message says the order qualifies,
 * not that shipping is free everywhere. Overpromising at the cart is how a
 * good delivery becomes a complaint.
 */
(function () {
  'use strict';

  var THRESHOLD = 24900;           /* agorot — ₪249, matching the live discount */
  var MOUNTED = 'data-nadi-ship-mounted';

  /* ONE BAR PER CART SURFACE, and the first version got this wrong.
   *
   * It listed five host selectors and guarded each host with a "已 mounted"
   * flag. In the drawer, two of those selectors matched elements NESTED inside
   * each other — the drawer body and the item list within it — so each was a
   * different host, each passed its own guard, and the customer saw the free
   * shipping message twice, stacked. Nadav caught it in a screenshot.
   *
   * The guard has to be per SURFACE, not per element. There are exactly two
   * surfaces a cart can appear on: the cart page and the drawer. Everything
   * below is scoped to a root, a root is only ever given one bar, and a final
   * sweep removes any duplicate that somehow lands in the same root anyway. */
  var ROOTS = ['.t4s-cartPage__form', '#t4s-mini_cart', '[id*="mini_cart"]', '.t4s-drawer__cart'];

  /* Where inside a root the bar should go, best first. The root itself is the
     fallback, which is what the cart page uses. */
  var INNER = ['.t4s-drawer__content', '.t4s-mini_cart__items', 'form'];

  var state = { total: null };

  function shekels(agorot) {
    return '₪' + Math.ceil(agorot / 100).toLocaleString('en-US');
  }

  function build() {
    var el = document.createElement('div');
    el.className = 'nadi-ship';
    el.setAttribute('data-nadi-ship', '');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML =
      '<p class="nadi-ship__msg"></p>' +
      '<div class="nadi-ship__track"><span class="nadi-ship__fill"></span></div>';
    return el;
  }

  function paint(el, total) {
    var msg = el.querySelector('.nadi-ship__msg');
    var fill = el.querySelector('.nadi-ship__fill');
    var pct = Math.max(0, Math.min(100, (total / THRESHOLD) * 100));

    if (total >= THRESHOLD) {
      el.setAttribute('data-qualified', '');
      msg.innerHTML = 'ההזמנה שלך זכאית ל<strong>משלוח חינם</strong>';
    } else {
      el.removeAttribute('data-qualified');
      msg.innerHTML = 'עוד <strong>' + shekels(THRESHOLD - total) + '</strong> ותקבל משלוח חינם';
    }
    fill.style.width = pct.toFixed(1) + '%';
  }

  function roots() {
    var found = [];
    ROOTS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (r) {
        if (found.indexOf(r) === -1) found.push(r);
      });
    });
    /* A drawer can match more than one ROOT selector and one root can contain
       another. Keep only the outermost of any nested pair, so the drawer is
       one surface rather than two. */
    return found.filter(function (r) {
      return !found.some(function (other) { return other !== r && other.contains(r); });
    });
  }

  function sweep() {
    /* Self-healing: whatever happened above, no root ends up with two bars. */
    roots().forEach(function (root) {
      var bars = root.querySelectorAll('[data-nadi-ship]');
      for (var i = 1; i < bars.length; i++) {
        var host = bars[i].parentElement;
        if (host) host.removeAttribute(MOUNTED);
        bars[i].remove();
      }
    });
  }

  function mountAll() {
    if (state.total === null) return;
    /* An empty cart has nothing to make progress towards. */
    if (state.total <= 0) return;

    roots().forEach(function (root) {
      if (root.querySelector('[data-nadi-ship]')) return;   /* this surface already has one */
      var host = null;
      for (var i = 0; i < INNER.length && !host; i++) host = root.querySelector(INNER[i]);
      if (!host) host = root;
      var el = build();

      /* NEVER INSIDE A LINE-ITEM CONTAINER, and this one cost a live bug.
       *
       * The bar used to go in as firstChild of .t4s-mini_cart__items — the very
       * element Kalles counts line items in. With one item in the drawer the DOM
       * became:
       *
       *     1  DIV.nadi-ship            <- this bar
       *     2  DIV.t4s-mini_cart__item  <- the actual item
       *
       * so the theme derived the line number from the child index and POSTed
       * {"line":2,"quantity":0} to /cart/change.js. Shopify replied 422,
       * "הפרמטר line אינו חוקי" — the cart has one line, and it is line 1. The
       * customer pressed the bin and nothing happened.
       *
       * With two or more items it is worse than nothing happening: every index
       * is shifted by one, so removing the first item removes the second.
       *
       * The bar therefore goes BEFORE such a container, never inside it. Being a
       * sibling costs nothing visually and keeps the theme arithmetic honest. */
      var LINE_ITEM = ".t4s-mini_cart__item, .t4s-cart__item, [class*='cart__item']";
      if (host.querySelector(LINE_ITEM) && host.parentNode && host !== root) {
        host.parentNode.insertBefore(el, host);
      } else {
        host.insertBefore(el, host.firstChild);
      }
      host.setAttribute(MOUNTED, '');
      paint(el, state.total);
    });
    sweep();
  }

  function repaintAll() {
    var bars = document.querySelectorAll('[data-nadi-ship]');
    if (state.total !== null && state.total <= 0) {
      bars.forEach(function (b) {
        var host = b.parentElement;
        if (host) host.removeAttribute(MOUNTED);
        b.remove();
      });
      return;
    }
    bars.forEach(function (b) { paint(b, state.total); });
  }

  var pending = null;
  function refresh() {
    if (pending) return pending;
    pending = fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.total_price === 'number') {
          state.total = cart.total_price;
          mountAll();
          repaintAll();
        }
      })
      .catch(function () { /* a failed read must never break the cart */ })
      .then(function () { pending = null; });
    return pending;
  }

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /* fetch */
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var call = nativeFetch.apply(this, arguments);
      if (CART_WRITE.test(url)) {
        call.then(function () { setTimeout(refresh, 60); }, function () {});
      }
      return call;
    };
  }

  /* XMLHttpRequest, for anything still using it */
  if (window.XMLHttpRequest) {
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (CART_WRITE.test(String(url || ''))) {
        this.addEventListener('load', function () { setTimeout(refresh, 60); });
      }
      return open.apply(this, arguments);
    };
  }

  /* The drawer is built lazily — watch for it instead of assuming. */
  var mo = new MutationObserver(function () { mountAll(); });

  function start() {
    refresh();
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
